import React, { createContext, useContext, useEffect, useState } from "react";
import { makeNamespace, storageGet, storageSet } from "../utils/storage";

// ENG-014: per-user "send the voice question automatically once I stop speaking" preference.
// Same per-user/per-origin storage as ExampleLanguageContext, but without an expiry: this is a
// deliberate choice the user made, not a transient UI state. Default off.

interface VoiceAutoSendContextType {
    enabled: boolean;
    toggle: () => void;
    // ENG-015: false when the admin turned voice off for this client's channel — the chat sets it
    // from GET /n8n-config so the profile menu can hide the toggle along with the mic.
    available: boolean;
    setAvailable: (available: boolean) => void;
    // ENG-034: true when the widget host fixed `enabled` via the init option `autoSend` — the
    // value then comes from the host, not per-user storage, and the profile menu hides its
    // toggle (there is nothing left for the employee to choose).
    hostControlled: boolean;
}

const VoiceAutoSendContext = createContext<VoiceAutoSendContextType | undefined>(undefined);

const STORAGE_SCHEMA_V = 1;

const deriveUserId = (): string => {
    try {
        const cfg = (window as any).PublicWidgetConfig;
        return cfg?.userId || "default";
    } catch {
        return "default";
    }
};

const getStorageKey = (): string => {
    const ns = makeNamespace(window.location.origin, deriveUserId(), STORAGE_SCHEMA_V);
    return ns.key("voice_auto_send");
};

const deriveInitialEnabled = (): boolean => {
    try {
        return storageGet<boolean>(getStorageKey()) === true;
    } catch {
        return false;
    }
};

// ENG-034: `autoSend` is the widget's init option (see widget.tsx WidgetConfig.autoSend), passed
// straight through by both <VoiceAutoSendProvider> render sites. When it is a boolean, it
// overrides the per-user preference for every employee of this widget and is never persisted to
// storage — the host owns it, not the browser. Omitted (undefined, the standalone app's case):
// today's behavior, unchanged.
export const VoiceAutoSendProvider: React.FC<{ children: React.ReactNode; autoSend?: boolean }> = ({ children, autoSend }) => {
    const [storedEnabled, setStoredEnabled] = useState<boolean>(deriveInitialEnabled);
    const [available, setAvailable] = useState<boolean>(true);
    const hostControlled = typeof autoSend === "boolean";
    const enabled = hostControlled ? (autoSend as boolean) : storedEnabled;

    useEffect(() => {
        if (hostControlled) {
            // The host's value is not a per-user preference — never write it into storage.
            return;
        }
        try {
            storageSet(getStorageKey(), storedEnabled, { schemaVersion: STORAGE_SCHEMA_V });
        } catch {}
    }, [storedEnabled, hostControlled]);

    const toggle = () => {
        if (hostControlled) {
            // The host owns the value; the profile menu hides the toggle in this case anyway.
            return;
        }
        setStoredEnabled(current => !current);
    };

    return <VoiceAutoSendContext.Provider value={{ enabled, toggle, available, setAvailable, hostControlled }}>{children}</VoiceAutoSendContext.Provider>;
};

// Unlike useExampleLanguage this does not throw without a provider: QuestionInput (the chat's
// input) must never crash over an optional preference — it just behaves as "off".
const NO_PROVIDER: VoiceAutoSendContextType = {
    enabled: false,
    toggle: () => undefined,
    available: true,
    setAvailable: () => undefined,
    hostControlled: false
};

export const useVoiceAutoSend = (): VoiceAutoSendContextType => useContext(VoiceAutoSendContext) ?? NO_PROVIDER;
